import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { type ViewEventData } from '@databases/analytics/src/types';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { DatabaseChangeType } from '@databases/types/redux';
import { createView, getViewById } from '@databases/utils/database/view/view';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import { getUniqueViewName, isUniqueViewName } from '../utils/viewName';

import useView from './useView';

type SaveAsNewViewOptions = {
  analyticsSource: ViewEventData['source'];
  newViewName?: string;
};

type UseSaveAsNewViewOptions = {
  contentId: string;
};

export const useSaveAsNewView = ({ contentId }: UseSaveAsNewViewOptions) => {
  const { view, setViewId, unsavedView } = useView();

  const intl = useIntl();

  const dispatch = defaultStoreUtils.useDispatch();
  const client = useAnalytics();

  const [viewNames, savedViews] = defaultStoreUtils.useEqualSelector((state) => {
    const database = selectDatabaseByContentIdRequired(state, { contentId });
    const filteredViews = database.views.filter((v) => v.id !== view.id);
    return [filteredViews.map((view) => view.name.trim().toLowerCase()), database.views];
  });

  const originalView = useMemo(
    () => getViewById(savedViews, view.id),
    [savedViews, view.id]
  );

  return useCallback(
    ({ analyticsSource, newViewName }: SaveAsNewViewOptions) => {
      const newView = createView(view.settings);
      const isRenamed = newViewName ? newViewName !== originalView?.name : false;

      const getViewName = () => {
        if (isRenamed && newViewName) {
          return newViewName;
        }

        if (
          originalView &&
          isUniqueViewName(view.name, [...viewNames, originalView.name])
        ) {
          return view.name;
        } else {
          return getUniqueViewName(
            {
              ...newView,
              name: unsavedView ? '' : view.name,
            },
            [...savedViews.map((v) => v.name), view.name],
            intl
          );
        }
      };

      dispatch(
        addChangeToDraft(contentId, 'main', {
          type: DatabaseChangeType.ADD_SAVED_VIEW,
          view: {
            ...newView,
            name: getViewName(),
          },
        })
      );

      client.sendDatabaseCreateViewEvent({
        contentId: contentId,
        databaseContainer: getDatabaseContainerType(),
        viewId: newView.id,
        source: analyticsSource,
        layoutType: newView.settings.layout.type,
      });

      setViewId(newView.id);
    },
    [
      client,
      contentId,
      dispatch,
      intl,
      originalView,
      savedViews,
      setViewId,
      unsavedView,
      view.name,
      view.settings,
      viewNames,
    ]
  );
};
